/**
 * CaptureToastComponent
 *
 * Post-capture toast: slides up from the bottom-center after the user releases
 * a capture. Shows the cropped thumbnail, an editable caption (wired to a
 * reactive FormControl per the project's mock-data-into-form-controls rule),
 * and 4 action buttons (Annotate / Share / Link / Done). Auto-dismisses after
 * 8 seconds unless the user interacts with the caption input.
 *
 * Designed as a *singleton* host-mounted component: the host binds [screenshot]
 * to a `pendingCapture` property; setting it to null hides the toast.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { Screenshot } from '../screenshots.types';
import { TOAST_AUTO_DISMISS_MS } from '../screenshots.constants';

@Component({
  standalone: false,
  selector: 'app-capture-toast',
  templateUrl: './capture-toast.component.html',
  styleUrls: ['./capture-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translate(-50%, 24px)' }),
        animate('250ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'translate(-50%, 0)' })),
      ]),
      transition(':leave', [
        animate('180ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0, transform: 'translate(-50%, 16px)' })),
      ]),
    ]),
  ],
})
export class CaptureToastComponent implements OnChanges, OnDestroy {
  /** Bound by the host. When null, the toast is hidden. */
  @Input() screenshot: Screenshot | null = null;

  /** Overridable per-host auto-dismiss timeout. */
  @Input() autoDismissMs: number = TOAST_AUTO_DISMISS_MS;

  @Output() annotate = new EventEmitter<void>();
  @Output() share = new EventEmitter<void>();
  @Output() link = new EventEmitter<void>();
  @Output() done = new EventEmitter<void>();
  /** Emits debounced caption changes so the host can persist via the service. */
  @Output() captionChange = new EventEmitter<string>();

  // Caption field — MOCK-DATA flows through this FormControl, not direct binding.
  public captionControl = new FormControl<string>('', { nonNullable: true });

  private dismissSub?: Subscription;
  private captionSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef) {
    // Debounce caption commits so we don't fire on every keystroke.
    this.captionSub = this.captionControl.valueChanges
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe((value) => this.captionChange.emit(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['screenshot']) return;
    const next = this.screenshot;
    if (next) {
      // Patch the FormControl from the (mock-or-real) data source, never bind raw.
      this.captionControl.setValue(next.caption ?? '', { emitEvent: false });
      this.restartDismissTimer();
    } else {
      this.cancelDismissTimer();
    }
  }

  ngOnDestroy(): void {
    this.cancelDismissTimer();
    this.captionSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** User clicked Annotate — flush caption then signal host. */
  onAnnotate(): void {
    this.cancelDismissTimer();
    this.commitCaption();
    this.annotate.emit();
  }

  onShare(): void {
    this.cancelDismissTimer();
    this.commitCaption();
    this.share.emit();
  }

  onLink(): void {
    this.cancelDismissTimer();
    this.commitCaption();
    this.link.emit();
  }

  onDone(): void {
    this.cancelDismissTimer();
    this.commitCaption();
    this.done.emit();
  }

  /** Caption field focused — pause the auto-dismiss so we don't yank the toast. */
  onCaptionFocus(): void {
    this.cancelDismissTimer();
  }

  /** Caption blurred — restart auto-dismiss. */
  onCaptionBlur(): void {
    this.restartDismissTimer();
  }

  /**
   * Build the future-API payload from the FormControl + bound data, then
   * delegate persistence to the host (which routes to ScreenshotsService.saveData).
   */
  private commitCaption(): void {
    // Flush any pending debounced value immediately.
    this.captionChange.emit(this.captionControl.value);
  }

  private restartDismissTimer(): void {
    this.cancelDismissTimer();
    if (!this.screenshot || this.autoDismissMs <= 0) return;
    this.dismissSub = timer(this.autoDismissMs)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.done.emit();
        this.cdr.markForCheck();
      });
  }

  private cancelDismissTimer(): void {
    this.dismissSub?.unsubscribe();
    this.dismissSub = undefined;
  }
}
